"use client";

import * as React from "react";
import { ArrowRight, ChevronLeft, ChevronRight, FlaskConical, Loader2, LockKeyhole, RotateCw, Search, SearchX } from "lucide-react";
import { Link } from "@/components/common/Link";
import { VialImage } from "@/components/common/VialImage";
import { Button } from "@/components/ui/button";
import { getProductImageAlt, resolveProductMaxQuantity, useProductImageAlts } from "@/lib/productImageUtils";
import { AddToCartButton, EcommerceProductsProvider, ProductPrice, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WordPressUserAuth } from "@/integrations/wordpress/WordPressUserAuth";
const auth = new WordPressUserAuth();

/* -------------------------------------------------------------------------- */
/* HELPERS                                                                    */
/* -------------------------------------------------------------------------- */

function isVariableProduct(product: any) {
  const productType = typeof product?.type === "string" ? product.type.toLowerCase() : "";
  const variations = Array.isArray(product?.variations) ? product.variations : [];
  return productType === "variable" || variations.length > 0;
}

/* -------------------------------------------------------------------------- */
/* AUTOMATIC MG OPTION DETECTION                                              */
/* -------------------------------------------------------------------------- */

function extractMgLabels(value: unknown) {
  if (typeof value !== "string") {
    return [];
  }
  const text = value.trim();
  if (!text) {
    return [];
  }
  const matches = Array.from(text.matchAll(/(\d+(?:\.\d+)?)\s*mg\b/gi));
  if (matches.length === 0) {
    return [];
  }

  /*
   * Preserve combination strengths when they appear together in one value,
   * e.g. "10mg + 10mg". Otherwise return each strength independently.
   */
  if (matches.length > 1) {
    const firstIndex = matches[0].index ?? 0;
    const lastMatch = matches[matches.length - 1];
    const lastIndex = (lastMatch.index ?? 0) + lastMatch[0].length;
    const between = text.slice(firstIndex, lastIndex);
    if (/[+\/&]/.test(between)) {
      return [matches.map(match => `${match[1]}mg`).join(" + ")];
    }
  }
  return matches.map(match => `${match[1]}mg`);
}
function getProductStrengthOptions(product: any) {
  const foundValues: string[] = [];
  const addValue = (value: unknown) => {
    extractMgLabels(value).forEach(label => {
      if (!foundValues.includes(label)) {
        foundValues.push(label);
      }
    });
  };

  /* PRODUCT ATTRIBUTES */
  if (Array.isArray(product?.attributes)) {
    product.attributes.forEach((attribute: any) => {
      const attributeName = String(attribute?.name || attribute?.label || attribute?.slug || "").toLowerCase();
      const likelyStrengthAttribute = attributeName.includes("mg") || attributeName.includes("strength") || attributeName.includes("dose") || attributeName.includes("size");
      const options = Array.isArray(attribute?.options) ? attribute.options : [];
      if (likelyStrengthAttribute) {
        options.forEach((option: unknown) => addValue(option));
      } else {
        options.forEach((option: unknown) => {
          if (/\d+(?:\.\d+)?\s*mg\b/i.test(String(option))) {
            addValue(option);
          }
        });
      }
    });
  }

  /* VARIATION OBJECTS */
  if (Array.isArray(product?.variations)) {
    product.variations.forEach((variation: any) => {
      if (!variation || typeof variation !== "object") {
        return;
      }
      if (Array.isArray(variation?.attributes)) {
        variation.attributes.forEach((attribute: any) => {
          addValue(attribute?.option || attribute?.value || attribute?.name || "");
        });
      }
      addValue(variation?.name);
      addValue(variation?.title);
      addValue(variation?.slug);
    });
  }

  /* COMMON PRODUCT FIELDS */
  addValue(product?.name);
  addValue(product?.title);
  if (Array.isArray(product?.tags)) {
    product.tags.forEach((tag: any) => {
      addValue(tag?.name || tag?.slug || "");
    });
  }
  return foundValues.sort((a, b) => {
    const aNumber = Number(a.match(/\d+(?:\.\d+)?/)?.[0] || 0);
    const bNumber = Number(b.match(/\d+(?:\.\d+)?/)?.[0] || 0);
    return aNumber - bNumber;
  });
}
function getSearchableProductText(product: any) {
  const categories = Array.isArray(product?.categories) ? product.categories.map((category: any) => category?.name || category?.slug || "").join(" ") : "";
  const tags = Array.isArray(product?.tags) ? product.tags.map((tag: any) => tag?.name || tag?.slug || "").join(" ") : "";
  return [product?.title, product?.name, product?.sku, categories, tags].filter(Boolean).join(" ").toLowerCase();
}

/* -------------------------------------------------------------------------- */
/* LOADING STATE                                                              */
/* -------------------------------------------------------------------------- */

function CatalogLoadingState() {
  return <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4" aria-label="Loading products" aria-busy="true">
      {Array.from({
      length: 8
    }).map((_, index) => <div key={index} className="overflow-hidden rounded-[18px] border border-[#dfe8eb] bg-white">
            <div className="aspect-[0.92/1] animate-pulse bg-[#edf3f4]" />

            <div className="p-3 sm:p-5">
              <div className="h-3 w-2/3 animate-pulse rounded bg-[#e6edef]" />
              <div className="mt-3 h-2 w-1/2 animate-pulse rounded bg-[#e6edef]" />
              <div className="mt-10 h-5 w-1/3 animate-pulse rounded bg-[#e6edef]" />
              <div className="mt-4 h-9 w-full animate-pulse rounded bg-[#e6edef]" />
            </div>
          </div>)}
    </div>;
}
function getCatalogDisplayTitle(product: any) {
  const originalTitle = String(product?.title || product?.name || "").trim();
  if (!originalTitle) {
    return "";
  }

  /*
   * Strength is displayed separately in the mg bubble(s), so remove mg
   * strength text from the catalog title only. This does not rename the
   * WooCommerce product or alter the product page title.
   *
   * Examples:
   * "Semax-10mg" -> "Semax"
   * "Tirzepatide-60mg" -> "Tirzepatide"
   * "BPC-157 + TB-500 — 10 mg + 10 mg" -> "BPC-157 + TB-500"
   */
  return originalTitle.replace(/\s*(?:[-–—|,:]\s*)?(?:\d+(?:\.\d+)?\s*mg\b)(?:\s*(?:\+|\/|&)\s*\d+(?:\.\d+)?\s*mg\b)*\s*$/i, "").replace(/\s*[-–—|,:]\s*$/, "").trim();
}

/* -------------------------------------------------------------------------- */
/* PRODUCT CARD                                                               */
/* -------------------------------------------------------------------------- */

function CatalogProductCard({
  product
}: {
  product: any;
}) {
  const variableProduct = isVariableProduct(product);
  const strengthOptions = getProductStrengthOptions(product);
  const hasStrengthOptions = strengthOptions.length > 0;
  const resolvedMax = resolveProductMaxQuantity(product);
  const isZeroStock = resolvedMax !== undefined && resolvedMax < 1;
  const inStock = !isZeroStock && product?.is_in_stock !== false && product?.stock_status !== "outofstock" && product?.is_purchasable !== false;
  const maxQuantity = resolvedMax !== undefined && resolvedMax >= 1 ? resolvedMax : undefined;
  const productLink = product?.link || `/post/product/${product?.id}`;
  const displayTitle = getCatalogDisplayTitle(product);
  return <article className="group flex min-w-0 flex-col overflow-hidden rounded-[18px] border border-[#dfe8eb] bg-white transition-all duration-300 hover:-translate-y-0.5 hover:border-[#bcd4de] hover:shadow-[0_16px_45px_rgba(16,45,58,0.08)]">

      {/* IMAGE */}
      <Link to={productLink} aria-label={`View ${product.title}`} className="relative block overflow-hidden bg-[#edf3f4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#087db3]">
        <div className="relative aspect-[0.94/1] w-full overflow-hidden">

          {product.featured_image_url ? <VialImage src={product.featured_image_url} alt={getProductImageAlt(product, product.featured_image_url)} loading="lazy" decoding="async" className="h-full w-full object-contain object-center transition-transform duration-700 ease-out group-hover:scale-[1.035]" /> : <div className="flex h-full w-full items-center justify-center text-[#718087]">
              <FlaskConical className="size-8 sm:size-11" strokeWidth={1.15} aria-hidden="true" />
            </div>}

          <span className="absolute right-2 top-2 rounded-full border border-white/70 bg-white/85 px-2 py-1 text-[6px] font-semibold uppercase tracking-[0.11em] text-[#607078] backdrop-blur-md sm:right-3 sm:top-3 sm:px-2.5 sm:text-[8px]">
            RUO
          </span>

          {!inStock && <div className="absolute inset-0 flex items-center justify-center bg-[#edf3f4]/75 backdrop-blur-[1px]">
              <span className="rounded-full border border-[#d5e1e5] bg-white px-3 py-1.5 text-[7px] font-semibold uppercase tracking-[0.12em] text-[#56636a] sm:text-[9px]">
                Out of Stock
              </span>
            </div>}
        </div>
      </Link>

      {/* PRODUCT INFORMATION */}
      <div className="flex flex-1 flex-col px-3 pb-0 pt-3 sm:px-5 sm:pt-4">

        <h3 className="line-clamp-2 min-h-[38px] text-[16px] font-semibold leading-[1.16] tracking-[-0.03em] text-[#10191d] sm:min-h-[50px] sm:text-[21px]">
          <Link to={productLink} className="transition-colors hover:text-[#087db3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#087db3]">
            {displayTitle}
          </Link>
        </h3>

        <p className="mt-1.5 text-[7px] leading-4 text-[#87949a] sm:text-[9px]">
          For research use only
        </p>

        {/* -------------------------------------------------------------- */}
        {/* AUTOMATIC STRENGTH BUBBLES                                     */}
        {/* -------------------------------------------------------------- */}

        {hasStrengthOptions ? <div className="mt-3 flex min-h-[28px] flex-wrap items-center gap-1.5 sm:gap-2">

            {strengthOptions.map(strength => <Link key={strength} to={productLink} aria-label={`View ${product.title} ${strength} option`} className="inline-flex min-h-[30px] items-center justify-center rounded-full border border-[#c9dce4] bg-[#f8fbfc] px-4 text-[10px] font-semibold tracking-[0.02em] text-[#0a1218] transition-all hover:border-[#087db3] hover:bg-[#eef7fb] hover:text-[#087db3] sm:min-h-[34px] sm:px-4.5 sm:text-[12px]">
                  {strength}
                </Link>)}
          </div> : null}

        <div className="min-h-[10px] flex-1 sm:min-h-[16px]" />

        {/* PRICE */}
        <div className="flex items-end justify-between gap-2 pb-3 sm:pb-4">

          <div className="min-w-0">
            {variableProduct ? <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-[#087db3] sm:text-[10px]">
                View options
              </span> : <ProductPrice product={product} className="text-[16px] font-semibold tracking-[-0.035em] text-[#0a1218] sm:text-[22px]" />}
          </div>

          <span className={["shrink-0 text-[6px] font-medium sm:text-[8px]", inStock ? "text-[#718087]" : "text-[#9a7777]"].join(" ")}>
            {inStock ? "In stock" : "Unavailable"}
          </span>
        </div>

        {/* ACTION BAR */}
        <div className="-mx-3 mt-auto grid grid-cols-2 border-t border-[#e3eaed] bg-[#f7fafb] sm:-mx-5">

          <Link to={productLink} className="group/details flex h-10 items-center justify-center gap-1 border-r border-[#e3eaed] px-1 text-[8px] font-semibold text-[#56636a] transition-colors hover:bg-white hover:text-[#087db3] sm:h-12 sm:gap-1.5 sm:text-[10px]">
            View item

            <ArrowRight className="hidden size-2.5 transition-transform group-hover/details:translate-x-0.5 sm:block sm:size-3" aria-hidden="true" />
          </Link>

          {!inStock ? <Link to={productLink} className="flex h-10 items-center justify-center px-1 text-[8px] font-semibold text-[#718087] transition-colors hover:bg-white hover:text-[#087db3] sm:h-12 sm:text-[10px]">
              View details
            </Link> : variableProduct ? <Link to={productLink} className="group/options flex h-10 items-center justify-center gap-1 px-1 text-[8px] font-semibold text-[#087db3] transition-colors hover:bg-white hover:text-[#055f8a] sm:h-12 sm:gap-1.5 sm:text-[10px]">
              Select options

              <span className="text-[10px] transition-transform group-hover/options:translate-x-0.5">
                +
              </span>
            </Link> : <AddToCartButton product={product} productId={product.id} variant="default" size="sm" text="Add to cart +" loadingText="Adding..." showQuantity={false} className="h-full w-full" buttonClassName="h-10 w-full rounded-none border-0 bg-transparent px-1 text-[8px] font-semibold text-[#087db3] shadow-none transition-colors hover:bg-white hover:text-[#055f8a] disabled:opacity-50 sm:h-12 sm:text-[10px]" disabled={!inStock} {...maxQuantity !== undefined ? {
          maxQuantity
        } : {}} />}
        </div>
      </div>
    </article>;
}

/* -------------------------------------------------------------------------- */
/* PRODUCT GRID                                                               */
/* -------------------------------------------------------------------------- */

function ProductCatalogConsumer({
  searchTerm
}: {
  searchTerm: string;
}) {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    isRefetching
  } = useEcommerceProducts();
  if (loading) {
    return <CatalogLoadingState />;
  }
  if (error) {
    return <div role="alert" className="rounded-[20px] border border-[#dbe6eb] bg-white px-6 py-12 text-center">
        <p className="text-sm text-[#56636a]">
          We couldn&apos;t load the product catalog.
        </p>

        <Button type="button" variant="outline" size="sm" onClick={() => refetch()} className="mt-4 rounded-full border-[#cbdbe2] bg-white text-[#0a1218]">
          <RotateCw className="mr-2 size-3.5" aria-hidden="true" />

          Retry
        </Button>
      </div>;
  }
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredProducts = normalizedSearch ? (products || []).filter((product: any) => getSearchableProductText(product).includes(normalizedSearch)) : products || [];
  if (filteredProducts.length === 0) {
    return <div className="rounded-[20px] border border-[#dbe6eb] bg-white px-6 py-14 text-center">

        <div className="mx-auto flex size-11 items-center justify-center rounded-full bg-[#eef5f8] text-[#087db3]">
          <SearchX className="size-5" aria-hidden="true" />
        </div>

        <h3 className="mt-4 text-lg font-semibold text-[#0a1218]">
          No products found
        </h3>

        <p className="mt-2 text-sm text-[#56636a]">
          Try another product name or keyword.
        </p>
      </div>;
  }
  const inStockProducts = filteredProducts.filter((product: any) => product.is_in_stock !== false);
  const outOfStockProducts = filteredProducts.filter((product: any) => product.is_in_stock === false);
  return <div className="space-y-12">

      {inStockProducts.length > 0 && <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
          {inStockProducts.map((product: any) => <CatalogProductCard key={product.id} product={product} />)}
        </div>}

      {outOfStockProducts.length > 0 && <div>
          <div className="mb-6 flex items-center gap-4">

            <div className="h-px flex-1 bg-[#dbe6eb]" />

            <span className="text-[8px] font-semibold uppercase tracking-[0.17em] text-[#718087] sm:text-[9px]">
              Currently Unavailable
            </span>

            <div className="h-px flex-1 bg-[#dbe6eb]" />

          </div>

          <div className="grid grid-cols-2 gap-3 opacity-70 sm:gap-5 lg:grid-cols-4">

            {outOfStockProducts.map((product: any) => <CatalogProductCard key={product.id} product={product} />)}

          </div>
        </div>}

      {totalPages > 1 && !normalizedSearch && <nav aria-label="Product catalog pagination" className="flex flex-col items-center justify-between gap-4 border-t border-[#dbe6eb] pt-8 sm:flex-row">
            <p className="text-[9px] uppercase tracking-[0.15em] text-[#718087]" aria-live="polite">
              Page {currentPage} /{" "}
              {totalPages}
            </p>

            <div className="flex items-center gap-2">

              <Button type="button" variant="outline" size="sm" onClick={() => prevPage()} disabled={!hasPrev || isRefetching} className="rounded-full border-[#cbdbe2] bg-white text-[#0a1218]">
                <ChevronLeft className="mr-1 size-3.5" aria-hidden="true" />

                Previous
              </Button>

              <Button type="button" variant="outline" size="sm" onClick={() => nextPage()} disabled={!hasNext || isRefetching} className="rounded-full border-[#cbdbe2] bg-white text-[#0a1218]">
                {isRefetching && <Loader2 className="mr-1 size-3.5 animate-spin" aria-hidden="true" />}

                Next

                <ChevronRight className="ml-1 size-3.5" aria-hidden="true" />
              </Button>

            </div>
          </nav>}
    </div>;
}

/* -------------------------------------------------------------------------- */
/* CATALOG                                                                    */
/* -------------------------------------------------------------------------- */

export default function Catalog() {
  useProductImageAlts();
  const [authChecked, setAuthChecked] = React.useState(false);
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  React.useEffect(() => {
    let mounted = true;
    const checkAuthentication = async () => {
      try {
        const userResult = await auth.getCurrentUser();
        if (mounted) {
          setIsLoggedIn(Boolean(userResult?.success && userResult?.data));
        }
      } catch {
        if (mounted) {
          setIsLoggedIn(false);
        }
      } finally {
        if (mounted) {
          setAuthChecked(true);
        }
      }
    };
    void checkAuthentication();
    return () => {
      mounted = false;
    };
  }, []);
  return <section id="productcatalog" data-nav="light" aria-labelledby="product-catalog-heading" className="relative overflow-hidden bg-[#f8fafb] pb-20 pt-8 text-[#0a1218] md:pb-24 md:pt-10">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8">

        {/* CATALOG HEADER */}
        <div className="mb-5 border-b border-[#dbe6eb] pb-5">

          <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">

            <div>
              <p className="text-[8px] font-semibold uppercase tracking-[0.2em] text-[#1479a7] sm:text-[9px]">
                The Collection
              </p>

              <h2 id="product-catalog-heading" className="mt-2 text-[24px] font-semibold tracking-[-0.045em] text-[#0a1218] sm:text-[30px]">
                {isLoggedIn ? "Research products" : "Catalog access"}
              </h2>
            </div>

            {isLoggedIn && <span className="text-[8px] font-semibold uppercase tracking-[0.16em] text-[#87969d]">
                Research Use Only
              </span>}

          </div>
        </div>

        {!authChecked ? <div className="flex min-h-[280px] items-center justify-center rounded-[20px] border border-[#dbe6eb] bg-white" aria-live="polite" aria-busy="true">
            <div className="text-center">

              <Loader2 className="mx-auto size-6 animate-spin text-[#087db3]" aria-hidden="true" />

              <p className="mt-4 text-sm text-[#56636a]">
                Verifying catalog
                access...
              </p>

            </div>
          </div> : !isLoggedIn ? <div className="rounded-[20px] border border-[#dbe6eb] bg-white px-6 py-14 text-center sm:px-10 sm:py-16">

            <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-[#cbdbe2] bg-[#eef5f8] text-[#087db3]">
              <LockKeyhole className="size-5" aria-hidden="true" />
            </div>

            <h3 className="mt-5 text-xl font-semibold tracking-[-0.025em] text-[#0a1218]">
              Account Access Required
            </h3>

            <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-[#56636a]">
              Sign in or create an account
              to access product listings,
              pricing, product details, and
              ordering.
            </p>

            <p className="mx-auto mt-3 max-w-xl text-xs leading-6 text-[#718087]">
              Research use only.
            </p>

          </div> : <EcommerceProductsProvider wp_query={{
        post_type: "product",
        per_page: 12
      }} className="products-catalog-wrapper">
            {/* SEARCH */}
            <div className="mb-7 flex items-center justify-between border-y border-[#dbe6eb] py-3">

              <div className="relative w-full max-w-[240px]">

                <Search className="pointer-events-none absolute left-3 top-1/2 z-10 size-3.5 -translate-y-1/2 text-[#718087]" strokeWidth={1.5} aria-hidden="true" />

                <input type="search" value={searchTerm} onChange={event => setSearchTerm(event.target.value)} placeholder="Search" aria-label="Search products" style={{
              backgroundColor: "#ffffff",
              color: "#0a1218",
              WebkitTextFillColor: "#0a1218",
              colorScheme: "light"
            }} className="h-10 w-full appearance-none rounded-[4px] border border-[#cfdadd] !bg-white pl-9 pr-3 text-[11px] !text-[#0a1218] shadow-none outline-none transition-colors placeholder:!text-[#7c898f] focus:border-[#087db3] focus:ring-0" />

              </div>

              {searchTerm && <button type="button" onClick={() => setSearchTerm("")} className="ml-4 shrink-0 text-[8px] font-semibold uppercase tracking-[0.14em] text-[#718087] transition-colors hover:text-[#087db3]">
                  Clear
                </button>}

            </div>

            <ProductCatalogConsumer searchTerm={searchTerm} />

          </EcommerceProductsProvider>}



      </div>
    </section>;
}